import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchHandcraftSubmissions,
  createHandcraftSubmission
} from '../../api/handcraftSubmissions';
import { skipActivity } from '../../api/activitySessions';
import { useTranslation } from '../../../../shared/i18n';

const isTestEnv = () =>
  (typeof window !== 'undefined' && window.railsEnv === 'test');

const styles = {
  wrapper: {
    background: 'linear-gradient(180deg, #fefaf3 0%, #fff 100%)',
    borderRadius: 20,
    padding: 24,
    border: '1px solid #f1e7d3'
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 20
  },
  handcraftTitle: {
    fontSize: 26,
    fontWeight: 800,
    color: '#1f2937',
    margin: 0
  },
  handcraftSubtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4
  },
  attemptPill: {
    background: '#fff5e0',
    border: '1px solid #f7cf7a',
    color: '#8a5300',
    borderRadius: 999,
    padding: '6px 14px',
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap'
  },
  panelGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 16,
    marginBottom: 20
  },
  panel: {
    background: '#fff',
    borderRadius: 16,
    padding: 16,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
    border: '1px solid #eef2f7',
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  panelLabel: {
    fontSize: 12,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#6b7280',
    fontWeight: 700,
    margin: 0
  },
  imageWrap: {
    background: '#0b1220',
    borderRadius: 12,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    aspectRatio: '4 / 3'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  emptyImage: {
    color: '#94a3b8',
    fontSize: 13,
    textAlign: 'center',
    padding: 24
  },
  primaryButton: {
    background: 'linear-gradient(180deg, #4f46e5 0%, #4338ca 100%)',
    color: '#fff',
    border: 0,
    borderRadius: 12,
    fontWeight: 700,
    padding: '12px 22px',
    boxShadow: '0 6px 14px rgba(79, 70, 229, 0.25)',
    fontSize: 15,
    cursor: 'pointer'
  },
  primaryButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    boxShadow: 'none'
  },
  secondaryButton: {
    background: '#fff',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: 12,
    fontWeight: 600,
    padding: '12px 18px',
    fontSize: 14,
    cursor: 'pointer'
  },
  linkButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#eef2ff',
    color: '#3730a3',
    border: '1px solid #e0e7ff',
    padding: '10px 16px',
    borderRadius: 12,
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none'
  },
  buttonRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap'
  },
  statusBanner: (variant) => {
    const palette = {
      success: { bg: '#ecfdf5', border: '#bbf7d0', text: '#065f46' },
      warning: { bg: '#fef3c7', border: '#fde68a', text: '#92400e' },
      danger: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' },
      neutral: { bg: '#f8fafc', border: '#e2e8f0', text: '#334155' }
    }[variant] || { bg: '#f8fafc', border: '#e2e8f0', text: '#334155' };
    return {
      background: palette.bg,
      border: `1px solid ${palette.border}`,
      color: palette.text,
      borderRadius: 14,
      padding: '14px 18px',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      marginTop: 8
    };
  }
};

/**
 * HandcraftSession - Kid-facing UI for the Handcraft activity.
 *
 * Camera state machine (dev / production):
 *   idle    → "Take Photo" button; no getUserMedia yet
 *   live    → stream running with a video preview + Capture / Cancel
 *   preview → stream stopped, still frame + Retake / Submit
 * Retake reopens the stream; Submit uploads and returns to idle.
 *
 * Test env (window.railsEnv === "test") uses a file input instead of the
 * camera so cucumber attaches a fixture without needing a real webcam.
 */
export const HandcraftSession = ({ session, activity, onComplete }) => {
  const { t } = useTranslation();
  const tk = useCallback(
    (key, opts) => t(`children.activity_sessions.handcraft.${key}`, opts),
    [t]
  );
  const [state, setState] = useState({ loading: true });
  const [mode, setMode] = useState('idle');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [latestSubmission, setLatestSubmission] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchHandcraftSubmissions(session.id);
      setState({ loading: false, ...data });
    } catch (error) {
      setState({ loading: false, error: error.message || tk('load_failed') });
    }
  }, [session.id, tk]);

  useEffect(() => { load(); }, [load]);

  // Poll only while OpenAI is grading the latest submission (status = "pending").
  // Once graded/errored, the initial load's response already reflects the final
  // state, so no further polling is needed.
  const latestStatus = state.submissions?.[state.submissions.length - 1]?.status;
  const isGrading = latestStatus === 'pending';
  useEffect(() => {
    if (!isGrading) return undefined;
    const timer = setInterval(() => { load(); }, 3000);
    return () => clearInterval(timer);
  }, [isGrading, load]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => stopCamera, [stopCamera]);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setMode('live');
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(tk('camera_unsupported'));
      setMode('idle');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      setCameraError(err.message || tk('camera_start_failed'));
      setMode('idle');
    }
  }, [tk]);

  const cancelCamera = useCallback(() => {
    stopCamera();
    setMode('idle');
  }, [stopCamera]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `handcraft-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setSelectedPhoto(file);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      stopCamera();
      setMode('preview');
    }, 'image/jpeg', 0.9);
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setSelectedPhoto(null);
    startCamera();
  };

  const handleFileFallback = (event) => {
    const file = event.target.files?.[0];
    if (file) setSelectedPhoto(file);
  };

  const handleSelfSkip = useCallback(async () => {
    try {
      const result = await skipActivity(session.id);
      if (result?.error) {
        alert(result.error.message || tk('skip_failed'));
        return;
      }
      window.location.reload();
    } catch (error) {
      alert(error.message || tk('skip_failed'));
    }
  }, [session.id, tk]);

  const handleSubmit = async () => {
    if (!selectedPhoto || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await createHandcraftSubmission(session.id, selectedPhoto);
      if (result.error) {
        alert(result.error.message);
        return;
      }
      setLatestSubmission(result.submission);
      setSelectedPhoto(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setMode('idle');
      if (result.session_completed) {
        onComplete({ status: 'success' });
        return;
      }
      await load();
    } catch (error) {
      alert(error.message || tk('submission_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div style={{ ...styles.wrapper, textAlign: 'center' }} data-testid="handcraft-loading">
        <div style={{ color: '#6b7280' }}>{tk('loading')}</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div style={styles.statusBanner('danger')}>
        {state.error}
      </div>
    );
  }

  const {
    handcraft,
    submissions = [],
    attempts_used: attemptsUsed = 0,
    max_attempts: maxAttempts = 3,
    can_submit: canSubmit = false
  } = state;

  const displaySubmission = latestSubmission || submissions[submissions.length - 1];
  const referenceSrc = handcraft?.cached_completed_image_url || handcraft?.completed_image_url;

  return (
    <section
      data-testid="handcraft-session"
      data-handcraft-id={handcraft?.id}
      style={styles.wrapper}
    >
      <header style={styles.header}>
        <div>
          <h3 data-testid="handcraft-name" style={styles.handcraftTitle}>
            {handcraft?.name}
          </h3>
          <div style={styles.handcraftSubtitle}>
            {tk('subtitle')}
          </div>
        </div>
      </header>

      <div style={{ marginBottom: 16 }}>
        <a
          href={handcraft?.instruction_url}
          target="_blank"
          rel="noopener"
          style={styles.linkButton}
          data-testid="handcraft-instructions-link"
        >
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M4 3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h5V3H4Zm7 0v14h5a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-5Zm-6 3h2a.75.75 0 0 1 0 1.5H5A.75.75 0 0 1 5 6Zm0 3h2a.75.75 0 0 1 0 1.5H5A.75.75 0 0 1 5 9Zm0 3h2a.75.75 0 0 1 0 1.5H5A.75.75 0 0 1 5 12Zm8-6h2a.75.75 0 0 1 0 1.5h-2A.75.75 0 0 1 13 6Zm0 3h2a.75.75 0 0 1 0 1.5h-2A.75.75 0 0 1 13 9Zm0 3h2a.75.75 0 0 1 0 1.5h-2A.75.75 0 0 1 13 12Z" />
          </svg>
          {tk('view_instructions')}
        </a>
      </div>

      <div style={styles.panelGrid}>
        <div style={styles.panel}>
          <p style={styles.panelLabel}>{tk('reference_craft')}</p>
          <div style={styles.imageWrap}>
            {referenceSrc ? (
              <img
                src={referenceSrc}
                alt={`${handcraft?.name} reference`}
                data-testid="handcraft-reference-image"
                style={styles.image}
              />
            ) : (
              <div style={styles.emptyImage}>{tk('no_reference_image')}</div>
            )}
          </div>
        </div>

        <div style={styles.panel}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <p style={styles.panelLabel}>{tk('your_submission')}</p>
            {canSubmit && (
              <span data-testid="handcraft-attempt-counter" style={styles.attemptPill}>
                {tk('attempt_counter', {
                  current: Math.min(attemptsUsed + 1, maxAttempts),
                  total: maxAttempts
                })}
              </span>
            )}
          </div>
          <div style={styles.imageWrap}>
            {mode === 'live' && !isTestEnv() && (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                data-testid="handcraft-camera-preview"
                style={styles.image}
              />
            )}
            {mode === 'preview' && previewUrl && (
              <img
                src={previewUrl}
                alt="Captured craft"
                data-testid="handcraft-photo-preview"
                style={styles.image}
              />
            )}
            {mode === 'idle' && (
              <div style={styles.emptyImage}>
                {canSubmit ? tk('tap_to_take_photo') : tk('waiting')}
              </div>
            )}
          </div>

          {canSubmit && !isTestEnv() && (
            <div data-testid="handcraft-camera">
              {cameraError && (
                <div data-testid="handcraft-camera-error" style={styles.statusBanner('danger')}>
                  {cameraError}
                </div>
              )}

              {mode === 'idle' && (
                <button
                  type="button"
                  style={styles.primaryButton}
                  data-testid="handcraft-take-photo-button"
                  onClick={startCamera}
                >
                  {tk('take_photo_button')}
                </button>
              )}

              {mode === 'live' && (
                <div style={styles.buttonRow}>
                  <button
                    type="button"
                    style={styles.primaryButton}
                    data-testid="handcraft-capture-button"
                    onClick={capturePhoto}
                  >
                    {tk('capture_button')}
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    data-testid="handcraft-cancel-camera-button"
                    onClick={cancelCamera}
                  >
                    {tk('cancel_camera_button')}
                  </button>
                </div>
              )}

              {mode === 'preview' && (
                <div style={styles.buttonRow}>
                  <button
                    type="button"
                    style={{
                      ...styles.primaryButton,
                      ...(isSubmitting ? styles.primaryButtonDisabled : {})
                    }}
                    data-testid="handcraft-submit-button"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? tk('submitting_button') : tk('submit_photo_button')}
                  </button>
                  <button
                    type="button"
                    style={styles.secondaryButton}
                    data-testid="handcraft-retake-button"
                    onClick={retake}
                  >
                    {tk('retake_button')}
                  </button>
                </div>
              )}
            </div>
          )}

          {canSubmit && isTestEnv() && (
            <div data-testid="handcraft-file-fallback">
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                {tk('test_mode_label')}
              </div>
              <input
                id="handcraft-photo-input"
                data-testid="handcraft-photo-input"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="form-control mb-3"
                onChange={handleFileFallback}
              />
              <button
                type="button"
                style={{
                  ...styles.primaryButton,
                  ...((!selectedPhoto || isSubmitting) ? styles.primaryButtonDisabled : {})
                }}
                data-testid="handcraft-submit-button"
                disabled={!selectedPhoto || isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? tk('submitting_button') : tk('submit_photo_button')}
              </button>
            </div>
          )}
        </div>
      </div>

      {isSubmitting && (
        <div style={styles.statusBanner('neutral')} data-testid="handcraft-processing">
          <span style={{ fontSize: 18 }}>⏳</span>
          <div>
            <strong>{tk('processing_title')}</strong>
            <div style={{ fontSize: 13, marginTop: 4 }}>{tk('processing_description')}</div>
          </div>
        </div>
      )}

      {!isSubmitting && displaySubmission && (
        <div data-testid="handcraft-last-result">
          <span data-testid="handcraft-last-status" style={{ display: 'none' }}>
            {displaySubmission.status}
          </span>

          {displaySubmission.status === 'errored' && (
            <div data-testid="handcraft-error-message" style={styles.statusBanner('danger')}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div>
                <strong>{tk('error_title')}</strong>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  <button
                    type="button"
                    data-testid="handcraft-can-skip"
                    onClick={handleSelfSkip}
                    style={{
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      color: '#991b1b',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    {tk('error_skip_link')}
                  </button>
                  {' '}{tk('error_skip_suffix')}
                </div>
              </div>
            </div>
          )}

          {displaySubmission.status === 'graded' && displaySubmission.failed && (
            <div data-testid="handcraft-try-again" style={styles.statusBanner('warning')}>
              <span style={{ fontSize: 18 }}>🎨</span>
              <div>
                <strong>{tk('try_again_title')}</strong>
                <div style={{ fontSize: 13, marginTop: 4 }}>
                  {tk('try_again_description')}
                </div>
              </div>
            </div>
          )}

          {displaySubmission.status === 'graded' && displaySubmission.passed && (
            <div style={styles.statusBanner('success')}>
              <span style={{ fontSize: 18 }}>🎉</span>
              <div>
                <strong>{tk('success_title')}</strong>
              </div>
            </div>
          )}
        </div>
      )}

      {!canSubmit && attemptsUsed >= maxAttempts && (
        <div data-testid="handcraft-max-attempts" style={styles.statusBanner('warning')}>
          <span style={{ fontSize: 18 }}>🙋</span>
          <div>
            <strong>{tk('max_attempts_title', { total: maxAttempts })}</strong>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {tk('max_attempts_description')}
            </div>
          </div>
        </div>
      )}

    </section>
  );
};
